import React from 'react';
import LogoLoader from '../ui/LogoLoader';
import './SaveIndicator.css';

/**
 * SaveIndicator Component
 * 
 * Shows activity status in the top-right corner:
 * - "Saving..." (spinning Plet logo)
 * - "Saved" (checkmark, fades out)
 * - "Moving Campaign..." (spinning Plet logo, for nesting operations)
 * - "Campaign Moved" (checkmark, fades out)
 * 
 * Note: Unsaved changes are shown as asterisk (*) in campaign title
 * 
 * @param {string} status - 'idle' | 'unsaved' | 'saving' | 'saved'
 * @param {boolean} isNesting - Whether a campaign nesting operation is in progress
 * @param {boolean} nestingComplete - Whether nesting just completed (shows success then fades)
 */
const SaveIndicator = ({ status = 'idle', isNesting = false, nestingComplete = false }) => {
  // Determine what to show (nesting takes priority over save status)
  const showNesting = isNesting;
  const showNestingComplete = nestingComplete && !isNesting;
  const showSaving = status === 'saving' && !showNesting;
  const showSaved = status === 'saved' && !showNesting && !showNestingComplete;
  
  // Don't show indicator if nothing active
  if (!showNesting && !showNestingComplete && !showSaving && !showSaved) {
    return null;
  }

  // Determine the CSS class
  let indicatorClass = 'save-indicator';
  if (showNesting) indicatorClass += ' save-indicator--saving';
  else if (showNestingComplete) indicatorClass += ' save-indicator--saved';
  else if (showSaving) indicatorClass += ' save-indicator--saving';
  else if (showSaved) indicatorClass += ' save-indicator--saved';

  return (
    <div className={indicatorClass}>
      {showNesting && (
        <>
          <LogoLoader size={20} />
          <span className="save-indicator__text">Moving Campaign...</span>
        </>
      )}
      
      {showNestingComplete && (
        <>
          <svg 
            className="save-indicator__checkmark" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor"
          >
            <polyline points="20 6 9 17 4 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="save-indicator__text">Campaign Moved</span>
        </>
      )}
      
      {showSaving && (
        <>
          <LogoLoader size={20} />
          <span className="save-indicator__text">Saving...</span>
        </>
      )}
      
      {showSaved && (
        <>
          <svg 
            className="save-indicator__checkmark" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor"
          >
            <polyline points="20 6 9 17 4 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="save-indicator__text">Saved</span>
        </>
      )}
    </div>
  );
};

export default SaveIndicator;

